import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { getRunnerConnection, resetRunnerSession } from "./api/codeRunning";

const AuthoringProjectPanels = ({ lesson_id }) => {
  const [connectionUrl, setConnectionUrl] = React.useState(null);
  const [loadingConnection, setLoadingConnection] = React.useState(true);
  const [connectionError, setConnectionError] = React.useState(null);
  const [runnerUnavailable, setRunnerUnavailable] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionError, setActionError] = React.useState("");
  const [actionInfo, setActionInfo] = React.useState("");

  const onGetRunnerConnectionSuccess = React.useCallback((info) => {
    setConnectionUrl(info?.url || null);
    setRunnerUnavailable(false);
    setConnectionError(null);
    setLoadingConnection(false);
  }, []);

  const onGetRunnerConnectionError = React.useCallback((error) => {
    const isUnavailable =
      error?.error_code === "RUNNER_UNAVAILABLE" ||
      error?.status === 503 ||
      error?.status === 404;
    setRunnerUnavailable(isUnavailable);
    setConnectionError(
      isUnavailable ? null : error?.error || "Error loading project."
    );
    setLoadingConnection(false);
  }, []);

  const loadConnection = () => {
    if (!lesson_id) return;

    setLoadingConnection(true);
    setRunnerUnavailable(false);
    setConnectionError(null);
    setActionError("");
    getRunnerConnection(
      lesson_id,
      onGetRunnerConnectionSuccess,
      onGetRunnerConnectionError
    );
  };

  const buildRunner = async () => {
    setActionLoading(true);
    setActionError("");
    setActionInfo("");
    try {
      const response = await fetch(`/api/lesson/${lesson_id}/deploy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force: false }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.error) {
        setActionError(data.error);
      } else {
        setActionInfo("Build started. Once complete, click Reload.");
      }
    } catch (error) {
      setActionError("Failed to start build.");
    }
    setActionLoading(false);
  };

  const resetAndReload = async () => {
    setActionLoading(true);
    setActionError("");
    setActionInfo("");
    try {
      await new Promise((resolve, reject) => {
        resetRunnerSession(lesson_id, resolve, reject);
      });
    } catch (error) {
      setActionError(error?.error || "Failed to reset runner session.");
    }
    setActionLoading(false);
    loadConnection();
  };

  React.useEffect(() => {
    loadConnection();
  }, [lesson_id]);

  if (loadingConnection)
    return (
      <div
        className={cn(
          "dq-flex dq-flex-col dq-items-center dq-justify-center",
          "dq-bg-black dq-h-full dq-w-full"
        )}
      >
        <CircularProgress size={36} />
      </div>
    );

  if (runnerUnavailable)
    return (
      <div
        className={cn(
          "dq-flex dq-flex-col dq-items-center dq-justify-center",
          "dq-bg-black dq-text-white dq-h-full dq-w-full"
        )}
      >
        <Stack spacing={2} sx={{ width: "100%", maxWidth: "28rem", p: 2 }}>
          <Alert severity="warning">Not available, please build.</Alert>
          {actionInfo && <Alert severity="info">{actionInfo}</Alert>}
          {actionError && <Alert severity="error">{actionError}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={buildRunner}
              disabled={actionLoading}
            >
              Build
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={resetAndReload}
              disabled={actionLoading}
            >
              Reset and Reload
            </Button>
            <Button
              variant="text"
              onClick={loadConnection}
              disabled={actionLoading}
            >
              Reload
            </Button>
          </Stack>
        </Stack>
      </div>
    );

  if (connectionError)
    return (
      <div
        className={cn(
          "dq-flex dq-flex-col dq-items-center dq-justify-center",
          "dq-bg-black dq-text-white dq-h-full dq-w-full"
        )}
      >
        <span className="dq-my-4">{connectionError}</span>
        <Button variant="contained" color="primary" onClick={loadConnection}>
          Retry
        </Button>
      </div>
    );

  if (!connectionUrl)
    return (
      <div
        className={cn(
          "dq-flex dq-flex-col dq-items-center dq-justify-center",
          "dq-bg-black dq-h-full dq-w-full"
        )}
      >
        <CircularProgress size={36} />
      </div>
    );

  return (
    <div className="dq-bg-black dq-flex dq-flex-col dq-h-full dq-w-full dq-absolute">
      <iframe
        src={connectionUrl}
        className="dq-h-full dq-w-full"
        frameBorder={0}
      />
    </div>
  );
};

AuthoringProjectPanels.propTypes = {
  lesson_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default AuthoringProjectPanels;
